import React from 'react'
import { api, ApiError, ApiValidationErrors } from '@/lib/api'
import { FieldError, FormAlert } from '@/components/FormError'
import { useBranding } from '@/lib/branding'

export default function ProfileTwoFactor() {
  const branding = useBranding()
  const [status, setStatus] = React.useState<any>(null)
  const [err, setErr] = React.useState<string | null>(null)
  const [errors, setErrors] = React.useState<ApiValidationErrors | undefined>()

  const [secret, setSecret] = React.useState<string | null>(null)
  const [otpauth, setOtpauth] = React.useState<string | null>(null)
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>([])
  const [code, setCode] = React.useState('')

  React.useEffect(() => {
    api.get('/xapi/auth/2fa/status').then(setStatus).catch(console.error)
  }, [])

  const startEnable = async () => {
    setErr(null); setErrors(undefined)
    try {
      const r:any = await api.post('/xapi/auth/2fa/enable')
      setSecret(r.secret); setOtpauth(r.otpauth); setRecoveryCodes(r.recovery_codes || [])
    } catch (e:any) {
      setErr(e.message)
    }
  }

  const confirmEnable = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null); setErrors(undefined)
    try {
      await api.post('/xapi/auth/2fa/confirm', { code })
      const st:any = await api.get('/xapi/auth/2fa/status')
      setStatus(st); setSecret(null); setOtpauth(null); setRecoveryCodes([]); setCode('')
    } catch (e:any) {
      if (e instanceof ApiError && e.validation) setErrors(e.validation)
      setErr(e.message)
    }
  }

  const disable = async () => {
    setErr(null)
    try {
      await api.post('/xapi/auth/2fa/disable')
      const st:any = await api.get('/xapi/auth/2fa/status')
      setStatus(st)
    } catch (e:any) {
      setErr(e.message)
    }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-3xl">
      <h1 className="text-2xl font-bold mb-2">Two-Factor Authentication</h1>
      <p className="text-sm text-gray-600 mb-6">Use an authenticator app to protect access to your {branding.product_name} account.</p>
      <FormAlert error={err} />
      {status?.enabled ? (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <p className="text-green-700">2FA is <strong>enabled</strong> on your account.</p>
          <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded" onClick={disable}>Disable 2FA</button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <p className="text-sm text-gray-700">Protect your account by adding a one-time code from an authenticator app.</p>
          {!secret ? (
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded" onClick={startEnable}>Enable 2FA</button>
          ) : (
            <div className="space-y-4">
              <div className="rounded border bg-gray-50 p-4">
                <p className="text-sm font-medium">Authenticator setup key</p>
                <code className="mt-2 block break-all text-sm">{secret}</code>
                {otpauth && <p className="mt-2 text-xs text-gray-500 break-all">{otpauth}</p>}
              </div>
              {recoveryCodes.length > 0 && (
                <div className="rounded border bg-amber-50 p-4">
                  <p className="text-sm font-medium text-amber-900">Recovery codes</p>
                  <div className="mt-2 grid grid-cols-2 gap-2 font-mono text-sm text-amber-900">
                    {recoveryCodes.map(code => <span key={code}>{code}</span>)}
                  </div>
                </div>
              )}
              <form onSubmit={confirmEnable} className="space-y-3">
                <label className="block text-sm font-medium">6-digit code
                  <input className="mt-1 w-full rounded border px-3 py-2" value={code} onChange={e=>setCode(e.target.value)} />
                  <FieldError errors={errors} name="code" />
                </label>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded" type="submit">Confirm and activate</button>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
